import { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import PublicLayout, { SEOHead, CTASection } from '../../components/PublicLayout';
import { Search, Clock, ArrowRight, Tag, ChevronLeft, ChevronRight } from 'lucide-react';
import { Input } from '../../components/ui/input';

const API = process.env.REACT_APP_BACKEND_URL;

const CLUSTER_LABELS = {
  'service-explainers': 'Service Explainers',
  'industry-specific': 'Industry Insights',
  'immigration-uscis': 'Immigration & USCIS',
  'compliance': 'Language Access & Compliance',
  'buyer-guides': 'Buyer Guides & Comparisons',
  'use-cases': 'Use Cases & How-To',
};

function PostCard({ post }) {
  const date = post.published_at ? new Date(post.published_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '';
  return (
    <Link to={`/blog/${post.slug}`} className="group block border rounded-xl overflow-hidden hover:shadow-lg transition-shadow" data-testid={`blog-card-${post.slug}`}>
      {post.featured_image && (
        <div className="aspect-[16/9] overflow-hidden bg-slate-100">
          <img src={post.featured_image} alt={post.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" loading="lazy" />
        </div>
      )}
      <div className="p-5">
        {post.cluster && <span className="text-xs font-medium text-blue-700 uppercase tracking-wide">{CLUSTER_LABELS[post.cluster] || post.cluster}</span>}
        <h2 className="text-lg font-bold text-slate-900 mt-1 mb-2 group-hover:text-blue-700 transition-colors line-clamp-2">{post.title}</h2>
        <p className="text-sm text-slate-600 line-clamp-2 mb-3">{post.excerpt}</p>
        <div className="flex items-center justify-between text-xs text-slate-500">
          <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{date}</span>
          <span className="text-blue-700 font-medium flex items-center gap-1 group-hover:underline">Read More <ArrowRight className="w-3 h-3" /></span>
        </div>
      </div>
    </Link>
  );
}

export default function BlogListPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [posts, setPosts] = useState([]);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);
  const [clusters, setClusters] = useState([]);
  const [search, setSearch] = useState(searchParams.get('search') || '');
  const activeCluster = searchParams.get('cluster') || '';
  const page = parseInt(searchParams.get('page') || '1', 10);

  useEffect(() => {
    const params = new URLSearchParams();
    params.set('page', page);
    params.set('limit', '12');
    if (activeCluster) params.set('cluster', activeCluster);
    if (searchParams.get('search')) params.set('search', searchParams.get('search'));
    fetch(`${API}/api/blog/posts?${params}`)
      .then(r => r.json())
      .then(d => { setPosts(d.posts || []); setTotal(d.total || 0); setPages(d.pages || 1); })
      .catch(() => {});
  }, [page, activeCluster, searchParams]);

  useEffect(() => {
    fetch(`${API}/api/blog/clusters`).then(r => r.json()).then(setClusters).catch(() => {});
  }, []);

  const setCluster = (c) => {
    const p = new URLSearchParams(searchParams);
    if (c) p.set('cluster', c); else p.delete('cluster');
    p.set('page', '1');
    setSearchParams(p);
  };

  const doSearch = (e) => {
    e.preventDefault();
    const p = new URLSearchParams(searchParams);
    if (search.trim()) p.set('search', search.trim()); else p.delete('search');
    p.set('page', '1');
    setSearchParams(p);
  };

  const goPage = (n) => {
    const p = new URLSearchParams(searchParams);
    p.set('page', n);
    setSearchParams(p);
    window.scrollTo(0, 0);
  };

  return (
    <PublicLayout>
      <SEOHead
        title="Blog | Interpreter Services — Language Access Insights & Guides"
        description="Expert articles on interpreter services, language access compliance, USCIS immigration interpreting, healthcare interpreting, and how to choose the right interpreting mode for your needs."
        canonical="https://interpreterservices.us/blog"
      />

      <section className="bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 text-white py-16">
        <div className="max-w-6xl mx-auto px-6">
          <h1 className="text-4xl sm:text-5xl font-bold mb-4" data-testid="blog-h1">Blog</h1>
          <p className="text-lg text-blue-100 max-w-2xl">Expert insights on interpreter services, language access, compliance, and how to connect with the right interpreter for every situation.</p>
        </div>
      </section>

      <div className="max-w-6xl mx-auto px-6 py-10">
        {/* Search + Filters */}
        <div className="flex flex-col sm:flex-row gap-4 mb-8">
          <form onSubmit={doSearch} className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input placeholder="Search articles..." value={search} onChange={e => setSearch(e.target.value)} className="pl-9 bg-white text-slate-900 border-slate-300" data-testid="blog-search" />
          </form>
          <div className="flex flex-wrap gap-2">
            <button onClick={() => setCluster('')} className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${!activeCluster ? 'bg-blue-700 text-white' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'}`} data-testid="blog-filter-all">All</button>
            {clusters.map(c => (
              <button key={c.cluster} onClick={() => setCluster(c.cluster)} className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${activeCluster === c.cluster ? 'bg-blue-700 text-white' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'}`} data-testid={`blog-filter-${c.cluster}`}>
                {CLUSTER_LABELS[c.cluster] || c.cluster} ({c.count})
              </button>
            ))}
          </div>
        </div>

        {/* Post Grid */}
        {posts.length === 0 ? (
          <p className="text-center text-slate-500 py-16">No articles found. Check back soon for new content.</p>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="blog-grid">
            {posts.map(p => <PostCard key={p.slug} post={p} />)}
          </div>
        )}

        {/* Pagination */}
        {pages > 1 && (
          <div className="flex justify-center items-center gap-2 mt-10">
            <button disabled={page <= 1} onClick={() => goPage(page - 1)} className="p-2 rounded-lg hover:bg-slate-100 disabled:opacity-30"><ChevronLeft className="w-5 h-5" /></button>
            {Array.from({ length: pages }, (_, i) => i + 1).slice(Math.max(0, page - 3), page + 2).map(n => (
              <button key={n} onClick={() => goPage(n)} className={`w-9 h-9 rounded-lg text-sm font-medium ${n === page ? 'bg-blue-700 text-white' : 'hover:bg-slate-100 text-slate-700'}`}>{n}</button>
            ))}
            <button disabled={page >= pages} onClick={() => goPage(page + 1)} className="p-2 rounded-lg hover:bg-slate-100 disabled:opacity-30"><ChevronRight className="w-5 h-5" /></button>
          </div>
        )}

        <p className="text-center text-sm text-slate-500 mt-4">{total} article{total !== 1 ? 's' : ''}</p>
      </div>

      <CTASection />
    </PublicLayout>
  );
}
